import { Alert, AlertProps, Box, ClickAwayListener, IconButton, Snackbar, styled } from '@mui/material';
import React, { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';
import { GrFormClose } from 'react-icons/gr';

export interface AlertContextType {
    severity: AlertProps['color'];
    message: string;
    open: boolean;
    onClose: () => void;
}

const StyledSnackbar = styled(Snackbar)(() => ({
    minWidth: 350
}))

const SnackbarError: React.FC<AlertContextType> = (props) => {
    return (
        <StyledSnackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={props.open}
            onClose={props.onClose}
        >
            <Alert
                severity="error"
                sx={{ display: 'flex', alignItems: 'center', width: '460px', '.MuiAlert-action': { padding: 0, }, '.MuiAlert-icon': { color: '#D82829' } }}
                action={(
                    <IconButton
                        color="secondary"
                        sx={{ color: '#D82829' }}
                        size="small"
                        onClick={props.onClose}
                    >
                        <GrFormClose />
                    </IconButton>
                )}
            >
                {props.message}
            </Alert>
        </StyledSnackbar>
    );
}

const SnackbarSuccess: React.FC<AlertContextType> = (props) => {

    return (
        <StyledSnackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={props.open}
            autoHideDuration={3500}
            onClose={(event, reason) => {
                if (reason === 'clickaway') return;
                props.onClose();
            }}
        >
            <Alert
                severity={props.severity}
                sx={{ display: 'flex', alignItems: 'center', width: '460px' }}
            >
                {props.message}
            </Alert>
        </StyledSnackbar>
    );
}

const UpdateAlertContext = createContext(null);

export const AlertContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [snackbar, setSnackbar] = useState<Omit<AlertContextType, 'onClose'> | null>(null);

    const handleOnCloseSnackbar = React.useCallback(() => {
        setSnackbar({ ...snackbar, open: false });
    }, [snackbar]);

    const CustomSnackbarJSX = snackbar ?
        ((snackbar.severity === 'error') ?
            (
                <ClickAwayListener
                    mouseEvent="onMouseDown"
                    touchEvent="onTouchStart"
                    onClickAway={handleOnCloseSnackbar}
                >
                    <Box sx={{ position: 'relative' }}>
                        <SnackbarError {...snackbar} onClose={handleOnCloseSnackbar} />
                    </Box>
                </ClickAwayListener>
            ) : (
                <SnackbarSuccess {...snackbar} onClose={handleOnCloseSnackbar} />
            )) :
        null;

    return (
        <UpdateAlertContext.Provider value={setSnackbar}>
            {children}
            {CustomSnackbarJSX}
        </UpdateAlertContext.Provider>
    );
};

export const useAlertContext:
    () => Dispatch<SetStateAction<Omit<AlertContextType, 'onClose'>>> =
    () => useContext(UpdateAlertContext);

export const useErrorNotify:
    () => ((message: string) => void) =
    () => {
        const setAlert = useAlertContext();
        const call = React.useCallback((message: string) => {
            setAlert({
                message,
                severity: 'error',
                open: true
            });
        }, [setAlert]);
        return call;
    };

export const useSuccessNotify:
    () => ((message: string) => void) =
    () => {
        const setAlert = useAlertContext();
        const call = React.useCallback((message: string) => {
            setAlert({
                message,
                severity: 'success',
                open: true
            });
        }, [setAlert]);
        return call;
    };

export const useWarningNotify:
    () => ((message: string) => void) =
    () => {
        const setAlert = useAlertContext();
        const call = React.useCallback((message: string) => {
            setAlert({
                message,
                severity: 'warning',
                open: true
            });
        }, [setAlert]);
        return call;
    };

export const useInfoNotify:
    () => ((message: string) => void) =
    () => {
        const setAlert = useAlertContext();
        const call = React.useCallback((message: string) => {
            setAlert({
                message,
                severity: 'info',
                open: true
            });
        }, [setAlert]);
        return call;
    };
